import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Award, Check, X, Clock, FileText, Scan, AlertCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPORTS_OPTIONS = [
  'cricket', 'football', 'tennis', 'badminton', 'table-tennis',
  'athletics', 'swimming', 'boxing', 'weightlifting', 'billiards',
];

const PLAYING_LEVELS = [
  'school', 'college', 'club_divisions', 'district', 'state',
  'national', 'international', 'olympics', 'other_international'
];

function CoachCredentials() {
  const [credentials, setCredentials] = useState({ coaching_licenses: [], playing_credentials: [] });
  const [showAddLicense, setShowAddLicense] = useState(false);
  const [showAddPlaying, setShowAddPlaying] = useState(false);
  const [showOCRUpload, setShowOCRUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ocrHistory, setOcrHistory] = useState([]);
  const [ocrResult, setOcrResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [licenseForm, setLicenseForm] = useState({
    sport: '',
    license_type: 'basic',
    license_number: '',
    issuing_authority: '',
    document_url: ''
  });
  
  const [playingForm, setPlayingForm] = useState({
    sport: '',
    level: 'school',
    proof_documents: '',
    description: ''
  });
  
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrCredentialType, setOcrCredentialType] = useState('coaching_license');
  
  useEffect(() => {
    fetchCredentials();
    fetchOcrHistory();
  }, []);
  
  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${API}/coaches/credentials/my`, { withCredentials: true });
      setCredentials(response.data);
    } catch (error) {
      console.error('Error fetching credentials:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOcrHistory = async () => {
    try {
      const response = await axios.get(`${API}/credentials/ocr-history`, { withCredentials: true });
      setOcrHistory(response.data);
    } catch (error) {
      console.error('Error fetching OCR history:', error);
    }
  };
  
  const handleOCRUpload = async (e) => {
    e.preventDefault();
    
    if (!ocrFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    setOcrResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', ocrFile);
      formData.append('credential_type', ocrCredentialType);
      
      const response = await axios.post(
        `${API}/credentials/ocr-verify`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setOcrResult(response.data);
      toast.success('Document processed successfully!');
      fetchOcrHistory();
    } catch (error) {
      console.error('Error processing document:', error);
      toast.error(error.response?.data?.detail || 'Failed to process document');
    } finally {
      setUploading(false);
    }
  };
  
  const handleAddLicense = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/coaches/credentials/add`,
        { credential_type: 'license', ...licenseForm },
        { withCredentials: true }
      );
      
      toast.success('Coaching license submitted for verification');
      setShowAddLicense(false);
      setLicenseForm({ sport: '', license_type: 'basic', license_number: '', issuing_authority: '', document_url: '' });
      fetchCredentials();
    } catch (error) {
      console.error('Error adding license:', error);
      toast.error('Failed to submit license');
    }
  };
  
  const handleAddPlaying = async (e) => {
    e.preventDefault();
    
    try {
      const proof_docs = playingForm.proof_documents.split(',').map(url => url.trim()).filter(url => url);
      
      await axios.post(
        `${API}/coaches/credentials/add`,
        { 
          credential_type: 'playing_level',
          sport: playingForm.sport,
          level: playingForm.level,
          proof_documents: proof_docs,
          description: playingForm.description
        },
        { withCredentials: true }
      );
      
      toast.success('Playing credentials submitted for verification');
      setShowAddPlaying(false);
      setPlayingForm({ sport: '', level: 'school', proof_documents: '', description: '' });
      fetchCredentials();
    } catch (error) {
      console.error('Error adding credentials:', error);
      toast.error('Failed to submit credentials');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="coach-credentials-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
            <Shield className="w-4 h-4 inline mr-1" strokeWidth={1.5} /> Verification Center
          </span>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            My <span className="gold-gradient">credentials</span>
          </h1>
        </div>
        
        {/* OCR Document Verification */}
        <div className="bg-dark-surface p-8 rounded-xl mb-8 border border-premium-gold/20" data-testid="ocr-section">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-3 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                <Scan className="w-7 h-7 text-premium-gold" strokeWidth={1.5} />
                AI document verification
              </h2>
              <p className="text-white/60 mt-1">Upload your certificates for automatic text extraction and verification</p>
            </div>
            <Button
              onClick={() => setShowOCRUpload(!showOCRUpload)}
              className="bg-premium-gold hover:opacity-90 text-deep-dark font-semibold px-6 py-2 rounded-lg"
              data-testid="toggle-ocr-upload"
            >
              {showOCRUpload ? 'Close' : 'Verify Document'}
            </Button>
          </div>
          
          {showOCRUpload && (
            <div className="bg-deep-dark/50 rounded-lg p-6 mb-6 border border-premium-gold/30/30">
              <form onSubmit={handleOCRUpload} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Document Type</label>
                    <select
                      value={ocrCredentialType}
                      onChange={(e) => setOcrCredentialType(e.target.value)}
                      className="w-full px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white rounded-lg focus:border-premium-gold focus:outline-none transition-all"
                      data-testid="ocr-type-select"
                    >
                      <option value="coaching_license">Coaching License</option>
                      <option value="certification">Professional Certification</option>
                      <option value="degree">Degree/Diploma</option>
                      <option value="sports_certificate">Sports Certificate</option>
                      <option value="experience_letter">Experience Letter</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Upload Document</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setOcrFile(e.target.files[0])}
                        className="hidden"
                        id="ocr-file-input"
                        data-testid="ocr-file-input"
                      />
                      <label 
                        htmlFor="ocr-file-input"
                        className="flex items-center gap-3 px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white rounded-lg cursor-pointer hover:border-premium-gold transition-colors"
                      >
                        <Upload className="w-5 h-5 text-premium-gold" />
                        <span className="truncate">{ocrFile ? ocrFile.name : 'Choose file (JPG, PNG, PDF)'}</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={uploading || !ocrFile}
                  className="w-full bg-premium-gold hover:bg-premium-gold-dark text-white font-bold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-glow-red"
                  data-testid="ocr-submit-btn"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing Document...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Scan className="w-5 h-5" />
                      Scan & Verify Document
                    </span>
                  )}
                </button>
              </form>
              
              {/* OCR Result Display */}
              {ocrResult && (
                <div className="mt-6 p-4 bg-dark-surface rounded-lg border border-premium-gold/30/50" data-testid="ocr-result">
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-white">
                    <FileText className="w-5 h-5 text-premium-gold" />
                    Extraction Results
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-deep-dark/50 rounded-lg">
                      <p className="text-white/50 text-sm">Confidence Score</p>
                      <p className={`font-bold text-lg ${ocrResult.confidence_score >= 80 ? 'text-emerald-400' : ocrResult.confidence_score >= 60 ? 'text-amber-400' : 'text-premium-gold'}`}>
                        {ocrResult.confidence_score}%
                      </p>
                    </div>
                    <div className="p-3 bg-deep-dark/50 rounded-lg">
                      <p className="text-white/50 text-sm">Detected Type</p>
                      <p className="font-bold text-lg capitalize text-white">{ocrResult.detected_document_type?.replace('_', ' ')}</p>
                    </div>
                    <div className="p-3 bg-deep-dark/50 rounded-lg">
                      <p className="text-white/50 text-sm">Status</p>
                      <p className={`font-bold ${ocrResult.verification_status === 'pending_review' ? 'text-amber-400' : 'text-premium-gold'}`}>
                        {ocrResult.verification_status?.replace('_', ' ').toUpperCase()}
                      </p>
                    </div>
                    <div className="p-3 bg-deep-dark/50 rounded-lg">
                      <p className="text-white/50 text-sm">Words Extracted</p>
                      <p className="font-bold text-lg text-white">{ocrResult.word_count}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-white/50 text-sm mb-2">Extracted Text Preview</p>
                    <div className="bg-deep-dark p-3 rounded-lg text-sm text-white/70 max-h-32 overflow-y-auto whitespace-pre-wrap border border-premium-gold/30/30">
                      {ocrResult.extracted_text || 'No text extracted'}
                    </div>
                  </div>
                  
                  {ocrResult.confidence_score < 60 && (
                    <div className="mt-4 flex items-start gap-2 text-amber-400 text-sm p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <p>Low confidence score. Try uploading a clearer image with better lighting and less blur.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* OCR History */}
          {ocrHistory.length > 0 && (
            <div>
              <h3 className="font-semibold text-white/70 mb-3">Recent Verifications</h3>
              <div className="space-y-2">
                {ocrHistory.slice(0, 3).map((item) => (
                  <div key={item.ocr_id} className="flex items-center justify-between bg-deep-dark/50 p-3 rounded-lg border border-premium-gold/30/30">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-white/40" />
                      <div>
                        <p className="font-medium text-sm text-white">{item.filename}</p>
                        <p className="text-xs text-white/50">{item.detected_document_type?.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${item.verification_status === 'approved' ? 'text-emerald-400' : item.verification_status === 'rejected' ? 'text-premium-gold' : 'text-amber-400'}`}>
                        {item.verification_status?.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-white/40">{item.confidence_score}% confidence</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Coaching Licenses */}
        <div className="bg-dark-surface p-8 rounded-xl border border-premium-gold/30/50 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              Coaching Licenses
            </h2>
            <button
              onClick={() => setShowAddLicense(!showAddLicense)}
              className="bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold px-6 py-2 rounded-lg transition-all hover:shadow-glow-red"
              data-testid="add-license-btn"
            >
              {showAddLicense ? 'Cancel' : 'Add License'}
            </button>
          </div>
          
          {showAddLicense && (
            <form onSubmit={handleAddLicense} className="mb-6 p-6 bg-deep-dark/50 border border-premium-gold/30 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  value={licenseForm.sport}
                  onChange={(e) => setLicenseForm({...licenseForm, sport: e.target.value})}
                  required
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white rounded-lg focus:border-premium-gold focus:outline-none transition-all"
                  data-testid="license-sport-select"
                >
                  <option value="">Select Sport</option>
                  {SPORTS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
                
                <select
                  value={licenseForm.license_type}
                  onChange={(e) => setLicenseForm({...licenseForm, license_type: e.target.value})}
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white rounded-lg focus:border-premium-gold focus:outline-none transition-all"
                >
                  <option value="basic">Basic</option>
                  <option value="advanced">Advanced</option>
                  <option value="professional">Professional</option>
                  <option value="international">International</option>
                </select>
                
                <input
                  type="text"
                  placeholder="License Number"
                  value={licenseForm.license_number}
                  onChange={(e) => setLicenseForm({...licenseForm, license_number: e.target.value})}
                  required
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white placeholder-white/40 rounded-lg focus:border-premium-gold focus:outline-none transition-all"
                  data-testid="license-number-input"
                />
                
                <input
                  type="text"
                  placeholder="Issuing Authority"
                  value={licenseForm.issuing_authority}
                  onChange={(e) => setLicenseForm({...licenseForm, issuing_authority: e.target.value})}
                  required
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white placeholder-white/40 rounded-lg focus:border-premium-gold focus:outline-none transition-all"
                />
                
                <input
                  type="url"
                  placeholder="Document URL (certificate/scan)"
                  value={licenseForm.document_url}
                  onChange={(e) => setLicenseForm({...licenseForm, document_url: e.target.value})}
                  required
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white placeholder-white/40 rounded-lg focus:border-premium-gold focus:outline-none transition-all md:col-span-2"
                />
              </div>
              <button
                type="submit"
                className="mt-4 bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold px-8 py-3 rounded-lg transition-all hover:shadow-glow-red"
                data-testid="submit-license-btn"
              >
                Submit License
              </button>
            </form>
          )}
          
          <div className="space-y-4">
            {credentials.coaching_licenses.length === 0 ? (
              <p className="text-white/50">No coaching licenses added yet</p>
            ) : (
              credentials.coaching_licenses.map((license, idx) => (
                <div key={idx} className="p-4 bg-deep-dark/50 border border-premium-gold/30/30 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-bold text-lg capitalize text-white">{license.sport} - {license.license_type}</p>
                    <p className="text-sm text-white/60">License: {license.license_number}</p>
                    <p className="text-sm text-white/60">Authority: {license.issuing_authority}</p>
                  </div>
                  <div className="text-right">
                    {license.verified ? (
                      <span className="flex items-center gap-2 text-emerald-400 font-semibold">
                        <Check className="w-5 h-5" /> Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-amber-400 font-semibold">
                        <Clock className="w-5 h-5" strokeWidth={1.5} /> Pending
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Playing Credentials */}
        <div className="bg-dark-surface p-8 rounded-xl border border-premium-gold/30/50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              Playing Credentials
            </h2>
            <button
              onClick={() => setShowAddPlaying(!showAddPlaying)}
              className="bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold px-6 py-2 rounded-lg transition-all hover:shadow-glow-red"
              data-testid="add-playing-btn"
            >
              {showAddPlaying ? 'Cancel' : 'Add Credential'}
            </button>
          </div>
          
          {showAddPlaying && (
            <form onSubmit={handleAddPlaying} className="mb-6 p-6 bg-deep-dark/50 border border-premium-gold/30 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select
                  value={playingForm.sport}
                  onChange={(e) => setPlayingForm({...playingForm, sport: e.target.value})}
                  required
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white rounded-lg focus:border-premium-gold focus:outline-none transition-all"
                >
                  <option value="">Select Sport</option>
                  {SPORTS_OPTIONS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
                
                <select
                  value={playingForm.level}
                  onChange={(e) => setPlayingForm({...playingForm, level: e.target.value})}
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white rounded-lg focus:border-premium-gold focus:outline-none transition-all"
                >
                  {PLAYING_LEVELS.map(level => (
                    <option key={level} value={level} className="capitalize">{level.replace('_', ' ')}</option>
                  ))}
                </select>
                
                <textarea
                  placeholder="Proof Document URLs (comma-separated: certificates, photos, media links)"
                  value={playingForm.proof_documents}
                  onChange={(e) => setPlayingForm({...playingForm, proof_documents: e.target.value})}
                  required
                  rows={2}
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white placeholder-white/40 rounded-lg focus:border-premium-gold focus:outline-none transition-all md:col-span-2"
                />
                
                <textarea
                  placeholder="Description (achievements, competitions, media appearances)"
                  value={playingForm.description}
                  onChange={(e) => setPlayingForm({...playingForm, description: e.target.value})}
                  rows={3}
                  className="px-4 py-3 bg-dark-surface border border-premium-gold/30 text-white placeholder-white/40 rounded-lg focus:border-premium-gold focus:outline-none transition-all md:col-span-2"
                />
              </div>
              <button
                type="submit"
                className="mt-4 bg-premium-gold hover:bg-premium-gold-dark text-white font-semibold px-8 py-3 rounded-lg transition-all hover:shadow-glow-red"
              >
                Submit Credentials
              </button>
            </form>
          )}
          
          <div className="space-y-4">
            {credentials.playing_credentials.length === 0 ? (
              <p className="text-white/50">No playing credentials added yet</p>
            ) : (
              credentials.playing_credentials.map((cred, idx) => (
                <div key={idx} className="p-4 bg-deep-dark/50 border border-premium-gold/30/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-lg capitalize text-white">{cred.sport} - {cred.level} Level</p>
                    {cred.verified ? (
                      <span className="flex items-center gap-2 text-emerald-400 font-semibold">
                        <Check className="w-5 h-5" /> Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-amber-400 font-semibold">
                        <Clock className="w-5 h-5" strokeWidth={1.5} /> Pending
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/70 mb-2">{cred.description}</p>
                  <p className="text-xs text-white/40">{cred.proof_documents?.length || 0} proof documents submitted</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoachCredentials;
